import axios from "@/axios-service";
import { ResponseContent, responseContent, paginatedResponseContent, Page  } from "../types";
import { Analysis, AnalysisDTO, AnalysisResponseDTO, AreaExpansionChartDTO, ScoreAreaComparativeDTO, ComparativeScoreChartDTO, YieldByYearDTO, AnalysisSummaryDTO, AnalysisYieldEvolutionDTO, LastYearYieldDTO, CropsStagesDTO, SummaryCropStagesDTO, AnalysisImageAreaData, AnalysesCount, AnalysesMonitoringCount, ProcessingCount, AnalysesListRow, AnalysesFilter, FarmMapFull } from "./types";
import { classToPlain } from "class-transformer";
import snakeCaseKeys from 'snakecase-keys'; 
import { buildParams } from "..";

const MS_URL = "senana/api/farms";
const MS_FARM_API_URL = "senana/farm";

const MS_URL_RESULT = 'senana/api/result';
const responseParser = responseContent(Analysis);
const analysisResponseDTOParser = responseContent(AnalysisResponseDTO);
const comparativeScoreResponseChartDTO = responseContent(ComparativeScoreChartDTO);
const areaExpansionResponseDTOParser = responseContent(AreaExpansionChartDTO);
const yieldByYearResponseDTOParser = responseContent(YieldByYearDTO);
const scoreAreaComparativeResponseDTOParser = responseContent(ScoreAreaComparativeDTO);
const analysisSummaryResponseDTOParser = responseContent(AnalysisSummaryDTO);
const analysisYieldEvolutionResponseDTOParser = responseContent(AnalysisYieldEvolutionDTO);
const lastYearYieldResponseDTOParser = responseContent(LastYearYieldDTO);
const cropsStagesResponseDTOParser = responseContent(CropsStagesDTO);
const summaryStagesResponseDTOParser = responseContent(SummaryCropStagesDTO);
const analysisImageAreaDataParser = responseContent(AnalysisImageAreaData);
const responseAsAnalysesCount = responseContent(AnalysesCount);
const responseAnalysesMonitoringCount = responseContent(AnalysesMonitoringCount);
const responseAsProcessingCount = responseContent(ProcessingCount);
const responseAsFarmMapFull = responseContent(FarmMapFull);



export const getByUuid = async (uuid: string): Promise<ResponseContent<Analysis[]>> => axios
  .get(`${MS_URL}/analysis/${uuid}`,)
  .then(responseParser)

export const getAnalysisByFarmUuid = async (farmUuid: string): Promise<ResponseContent<Analysis[]>> => axios
  .get(`${MS_URL}/${farmUuid}/analysis`,)
  .then(responseParser)

export const createAnalysis = async (
    analysisType : string,
    analysisDTO: AnalysisDTO
): Promise<ResponseContent<AnalysisResponseDTO>> => axios
    .post(`${MS_URL}/analysis/${analysisType}`, classToPlain(analysisDTO))
    .then(analysisResponseDTOParser);

export const deleteByUuid = async (uuid: string) => axios
  .delete(`${MS_URL}/analysis/${uuid}`)

export const findAreaExpansionByHarvestSeasonAndCropType = async (uuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_URL}/analysis/${uuid}/area-expansion`, { params: { harvestSeason, cropType }})
  .then(areaExpansionResponseDTOParser);

  export const findYieldByYearData = async (uuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_URL}/analysis/${uuid}/yield-by-year`, { params: { harvestSeason, cropType }})
  .then(yieldByYearResponseDTOParser);

  export const findScoreAreaComparativeDataByAnalysis = async (uuid: string, harvestSeason: string, cropType: string, yieldScoreType: string) => axios
  .get(`${MS_URL}/analysis/${uuid}/comparative/score-area`, { params: { harvestSeason, cropType, yieldScoreType }})
  .then(scoreAreaComparativeResponseDTOParser);

  export const getComparativeScoreByHarvestAndCropType = async (uuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_URL}/analysis/${uuid}/comparative/score`, { params: { harvestSeason, cropType }})
  .then(comparativeScoreResponseChartDTO);


  export const getAnalysisSummary = async (uuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_URL_RESULT}/analysis/${uuid}/summary`, { params: { harvestSeason, cropType }})
  .then(analysisSummaryResponseDTOParser);

  export const getFarmAnalysisYieldEvolution = async (farmAnalysisUuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_FARM_API_URL}/analysis/${farmAnalysisUuid}/yield-evolution`, { params: { harvestSeason, cropType }})
  .then(analysisYieldEvolutionResponseDTOParser);

  export const getLastYearYieldData = async (uuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_URL}/analysis/${uuid}/last-year-yield`, { params: { harvestSeason, cropType }})
  .then(lastYearYieldResponseDTOParser);

  export const getAnalysisCropsStages = async (uuid: string, harvestSeason: string, cropType: string) => axios
  .get(`${MS_FARM_API_URL}/analysis/${uuid}/farm-crop-stages`, { params: { harvestSeason, cropType }})
  .then(summaryStagesResponseDTOParser);


export const getAnalysisImageAreaData = async (analysisUuid: string) => axios
  .get(`${MS_FARM_API_URL}/analysis/${analysisUuid}/image-area-data`)
  .then(analysisImageAreaDataParser);

  export const getSentinelaPrimeLastImageInfo = async (farmAnalysisUuid: string) => axios
  .get(`${MS_FARM_API_URL}/analysis/${farmAnalysisUuid}/last-planet-image-relevance`)
  .then(analysisImageAreaDataParser);


export const getAnalysesCount = async (analysisType:string, onlyUserFarms:boolean): Promise<ResponseContent<AnalysesCount>> => axios
  .get(`${MS_URL}/analysis/${analysisType}/count`, { params: { onlyUserFarms }})
  .then(responseAsAnalysesCount);


  export const getProcessingCount = async (onlyUserFarms:boolean): Promise<ResponseContent<ProcessingCount>> => axios
  .get(`${MS_URL}/analysis/count`, { params: { onlyUserFarms }})
  .then(responseAsProcessingCount);


  export const getAnalysesStatus = async (page: Page, onlyUserFarms:boolean) => {
    return axios
    .get(`${MS_URL}/analysis/status`, { params: { onlyUserFarms }} )
    .then(response => paginatedResponseContent(snakeCaseKeys(response.data), AnalysesListRow))
  }

 export const getAllAnalysesStatusByFilter = async (filters: AnalysesFilter, page: Page ) => {
  const params = buildParams(filters, page);
   return axios 
   .get(`${MS_URL}/analysis/status`, params )
    .then(response => paginatedResponseContent(response.data, AnalysesListRow))
 }

export const getFarmMapFull = async (
  uuid: string
): Promise<ResponseContent<FarmMapFull>> =>
  axios.get(`${MS_URL}/${uuid}/brief`).then(responseAsFarmMapFull);

export const finishAnalysis = async (uuid: string) => axios
  .patch(`${MS_URL}/analysis/${uuid}/finished`, {finish: true})

export const updateMonitoring = async (uuid: string) => axios
  .put(`${MS_URL}/analysis/${uuid}`)
  .then(responseContent(Object));

export const updateAnalysis = async (uuid: string, harvestYear: number) => axios
  .put(`${MS_URL}/analysis/${uuid}?harvestYear=${harvestYear}`)
  .then(responseContent(Object));
